import React from 'react';
import InstrumentPanel from 'ff/debrief/layouts/InstrumentPanel';
import InstrumentAccordion from 'ff/debrief/components/instruments/InstrumentAccordion';
import InstrumentCluster from 'ff/debrief/components/instruments/InstrumentCluster';
import {
    useChrono,
    useCurrentAircraft,
    useCurrentDebrief,
} from 'ff/debrief/hooks';
import {
    formatValue,
    calculateCrosswindSpeed,
    formatSpeed,
    convertSpeed,
} from 'ff/debrief/utils';

import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Stack from '@mui/material/Stack';

const Instruments = () => {
    const { currentPoint } = useChrono();
    const { hasDebrief } = useCurrentDebrief();
    const { speedUnit } = useCurrentAircraft();
    if (!hasDebrief) return null;

    const speedValues = [
        `${formatSpeed(currentPoint?.indicatedAirSpeed, speedUnit)} IAS`,
        `${convertSpeed(currentPoint?.trueAirSpeed, speedUnit)} TAS`,
        `${convertSpeed(currentPoint?.groundSpeed, speedUnit)} GS`,
    ];

    const altitudeValues = [
        `${formatValue(currentPoint?.verticalSpeed)} fpm`,
        `${formatValue(currentPoint?.aboveGroundLevel)}' AGL`,
        `${formatValue(currentPoint?.altitudeFeet)}' MSL`,
    ];

    const attitudeValues = [
        `${formatValue(currentPoint?.bankDeg)}° bank`,
        `${formatValue(currentPoint?.magneticHeading, 0, true)}° HDG`,
        `${formatValue(currentPoint?.magneticCourse, 0, true)}° Track`,
        `${formatValue(currentPoint?.turnRate, 1)}° Per Sec`,
    ];

    const headwind = currentPoint?.headwind;
    let headwindIcon;
    if (headwind > 0) {
        headwindIcon = <ArrowDownwardIcon fontSize="small" />; // Headwind
    } else if (headwind < 0) {
        headwindIcon = <ArrowUpwardIcon fontSize="small" />; // Tailwind
    }

    const crosswind = calculateCrosswindSpeed(
        currentPoint?.windDirection,
        currentPoint?.magneticHeading,
        currentPoint?.windVelocity
    );
    let crosswindIcon = null;
    if (crosswind > 0) {
        crosswindIcon = <ArrowBackIcon fontSize="small" />; // Crosswind from Right
    } else if (crosswind < 0) {
        crosswindIcon = <ArrowForwardIcon fontSize="small" />; // Crosswind from Left
    }

    const windValues = [
        `${formatValue(currentPoint?.windVelocity)} kts wind`,
        `${formatValue(currentPoint?.windDirection, 0, true)}°`,
        <Stack
            component="span"
            key="headwind"
            direction="row"
            alignItems="center"
            gap={0.5}
        >
            {headwindIcon}
            {formatValue(headwind != null ? Math.abs(headwind) : null)} kts
        </Stack>,
        <Stack
            component="span"
            key="crosswind"
            direction="row"
            alignItems="center"
            gap={0.5}
        >
            {crosswindIcon}
            {formatValue(crosswind != null ? Math.abs(crosswind) : null)} kts
        </Stack>,
    ];

    return (
        <InstrumentPanel>
            <InstrumentAccordion>
                <InstrumentCluster values={speedValues} />
                <InstrumentCluster values={altitudeValues} />
                <InstrumentCluster values={attitudeValues} />
                <InstrumentCluster values={windValues} />
            </InstrumentAccordion>
        </InstrumentPanel>
    );
};
export default Instruments;
